/**
 * Claims Manager
 * 
 * Handles the management of claims data across the application:
 * - Stores claims in localStorage
 * - Adds new claims when submitted
 * - Renders claims in the appropriate tables
 * - Provides draft saving and resuming functionality
 * - Allows resetting claims to default examples
 */

const ClaimsManager = {
    // Key for storing claims in localStorage
    STORAGE_KEY: 'fleet_insurance_claims',

    // Key for storing draft claims
    DRAFTS_STORAGE_KEY: 'fleet_insurance_draft_claims',

    // Initial claims data (will be used if nothing exists in localStorage)
    initialClaims: [
        {
            claimId: "ABC123",
            clientName: "Sprint Courier Ltd",
            policyNumber: "POL-1001",
            status: "Submitted",
            lastUpdate: "21/03/25 10:03",
            incidentType: "Accident",
            vrn: "AB12 CDE"
        },
        {
            claimId: "DEF456",
            clientName: "Sprint Courier Ltd",
            policyNumber: "POL-1001",
            status: "Processing",
            lastUpdate: "20/03/25 14:22",
            incidentType: "Theft",
            vrn: "XY15 ZAB"
        },
        {
            claimId: "GHI789",
            clientName: "Value Car Rental UK",
            policyNumber: "POL-1002",
            status: "Approved",
            lastUpdate: "18/03/25 09:15",
            incidentType: "Accident",
            vrn: "CD34 EFG"
        },
        {
            claimId: "978XYZ",
            clientName: "Auto Driving School",
            policyNumber: "POL-1004",
            status: "Awaiting Repair",
            lastUpdate: "19/03/25 18:55",
            incidentType: "Windscreen",
            vrn: "UV12 WXY"
        },
        {
            claimId: "JKL012",
            clientName: "Fast-Food Deliveries",
            policyNumber: "POL-1003",
            status: "Completed",
            lastUpdate: "17/03/25 11:30",
            incidentType: "Vandalism",
            vrn: "KL78 MNO"
        }
    ],

    init: function () {
        console.log('Claims Manager initialized');

        // Set up initial data if it doesn't exist yet
        if (!localStorage.getItem(this.STORAGE_KEY)) {
            localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.initialClaims));
        }

        // IMPORTANT: Always log the current claims to the console for debugging
        console.log('Current claims in storage:', this.getAllClaims());

        // Detect current page and render claims accordingly
        this.renderClaimsForCurrentPage();

        // Make claim IDs clickable
        this.makeClaimIdsClickable();

        // Check for draft claim in URL
        this.checkForDraftClaimInURL();

        // Attach search functionality if applicable
        this.setupTableSearch();

        // Set up reset button
        this.setupResetButton();

        // Check for claim submission event
        this.setupClaimSubmissionHandler();

        // Set up auto-save functionality if on form page
        this.setupAutoSave();

        // Set up save draft button (add this line)
        this.setupSaveDraftButton();
    },

    // Fetch all claims from storage
    getAllClaims: function () {
        const claimsData = localStorage.getItem(this.STORAGE_KEY);
        return claimsData ? JSON.parse(claimsData) : [];
    },

    // Get a specific claim by ID
    getClaimById: function (claimId) {
        const claims = this.getAllClaims();
        return claims.find(claim => claim.claimId === claimId) || null;
    },

    // Add a new claim
    addClaim: function (claimData) {
        // Get existing claims
        const claims = this.getAllClaims();

        // Generate a claim ID if not provided
        if (!claimData.claimId) {
            claimData.claimId = this.generateClaimId();
        }

        // Set the current date/time for lastUpdate if not provided
        if (!claimData.lastUpdate) {
            claimData.lastUpdate = this.formatCurrentDateTime();
        }

        // Set default status if not provided
        if (!claimData.status) {
            claimData.status = "Submitted";
        }

        // CHANGE: Check if a claim with this ID already exists (whether draft or submitted)
        const existingIndex = claims.findIndex(claim => claim.claimId === claimData.claimId);

        if (existingIndex >= 0) {
            // If it's a draft being submitted, replace it
            if (claims[existingIndex].isDraft === true && claimData.submitted === true) {
                claims[existingIndex] = claimData;
            }
            // If it's a new submission for an existing claimId, replace only if not already submitted
            else if (!claims[existingIndex].submitted) {
                claims[existingIndex] = claimData;
            }
            // Otherwise add as a new claim (which shouldn't happen with our improved logic)
            else {
                claims.push(claimData);
            }
        } else {
            // Add to claims array as new
            claims.push(claimData);
        }

        // Save back to localStorage
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(claims));

        // After successful submission, remove from drafts if it was a draft
        this.deleteDraftClaim(claimData.claimId);

        console.log('Claim operation completed:', claimData);
        console.log('All claims after operation:', claims);

        return claimData;
    },

    // Generate a unique claim ID
    generateClaimId: function () {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
        const numbers = '0123456789';

        let id = '';

        // Generate 3 random letters
        for (let i = 0; i < 3; i++) {
            id += chars.charAt(Math.floor(Math.random() * chars.length));
        }

        // Generate 3 random numbers
        for (let i = 0; i < 3; i++) {
            id += numbers.charAt(Math.floor(Math.random() * numbers.length));
        }

        return id;
    },

    // Format current date and time in the format used by the application
    formatCurrentDateTime: function () {
        const now = new Date();
        const day = String(now.getDate()).padStart(2, '0');
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const year = String(now.getFullYear()).slice(-2);
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');

        return `${day}/${month}/${year} ${hours}:${minutes}`;
    },

    // Reset claims to initial examples only
    resetClaims: function () {
        // Save only the initial claims to localStorage
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.initialClaims));
        console.log('Claims reset to initial examples');

        // Refresh the claims display
        this.renderClaimsForCurrentPage();

        // Show a success message
        this.showResetSuccessMessage();
    },

    _showCustomToast: function (title, message, icon, bgColor, textColor) {
        // Create toast container if it doesn't exist
        let toastContainer = document.querySelector('.custom-toast-container');
        if (!toastContainer) {
            toastContainer = document.createElement('div');
            toastContainer.className = 'custom-toast-container';
            toastContainer.style.position = 'fixed';
            toastContainer.style.top = '20px';
            toastContainer.style.right = '20px';
            toastContainer.style.zIndex = '9999';
            document.body.appendChild(toastContainer);
        }

        // Create toast element - dark theme with glass effect
        const toast = document.createElement('div');
        toast.className = 'custom-toast';
        toast.style.backgroundColor = 'rgba(25, 35, 50, 0.85)'; // Dark blue semi-transparent
        toast.style.backdropFilter = 'blur(10px)';
        toast.style.WebkitBackdropFilter = 'blur(10px)'; // For Safari
        toast.style.color = '#fff';
        toast.style.borderRadius = '4px';
        toast.style.padding = '12px 20px';
        toast.style.marginBottom = '10px';
        toast.style.boxShadow = '0 4px 12px rgba(0,0,0,0.25)';
        toast.style.display = 'flex';
        toast.style.alignItems = 'center';
        toast.style.minWidth = '300px';
        toast.style.maxWidth = '400px';
        toast.style.animation = 'fadeIn 0.3s, fadeOut 0.3s 2.7s';
        toast.style.border = '1px solid rgba(255, 255, 255, 0.1)';

        // Add styles for fade animations if they don't exist yet
        if (!document.getElementById('custom-toast-styles')) {
            const styleEl = document.createElement('style');
            styleEl.id = 'custom-toast-styles';
            styleEl.textContent = `
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(-20px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                @keyframes fadeOut {
                    from { opacity: 1; transform: translateY(0); }
                    to { opacity: 0; transform: translateY(-20px); }
                }
            `;
            document.head.appendChild(styleEl);
        }

        // Create toast content - with amber accent
        toast.innerHTML = `
            <div style="display: flex; align-items: center; width: 100%;">
                <div style="margin-right: 12px; font-size: 1.2rem; color: #ffaa00;">
                    <i class="${icon}"></i>
                </div>
                <div style="flex-grow: 1;">
                    <div style="font-weight: bold; margin-bottom: 4px; color: #fff;">${title}</div>
                    <div style="color: rgba(255, 255, 255, 0.9);">${message}</div>
                </div>
                <div style="margin-left: 12px; cursor: pointer; color: rgba(255, 255, 255, 0.7);" class="toast-close">
                    <i class="ph ph-x"></i>
                </div>
            </div>
        `;

        // Add to container
        toastContainer.appendChild(toast);

        // Add close button functionality with hover effect
        const closeBtn = toast.querySelector('.toast-close');
        if (closeBtn) {
            closeBtn.addEventListener('mouseover', function () {
                this.style.color = '#fff';
            });
            closeBtn.addEventListener('mouseout', function () {
                this.style.color = 'rgba(255, 255, 255, 0.7)';
            });
            closeBtn.addEventListener('click', function () {
                toast.style.opacity = '0';
                toast.style.transform = 'translateY(-20px)';
                setTimeout(() => {
                    if (toast.parentNode) {
                        toast.parentNode.removeChild(toast);
                    }
                }, 300);
            });
        }

        // Auto remove after 3 seconds
        setTimeout(() => {
            toast.style.opacity = '0';
            toast.style.transform = 'translateY(-20px)';
            setTimeout(() => {
                if (toast.parentNode) {
                    toast.parentNode.removeChild(toast);
                }
            }, 300);
        }, 3000);
    },

    // Custom confirmation dialog helper - internal use only
    // This doesn't replace any existing function, it's a new helper
    _showCustomConfirmDialog: function (title, message, icon, confirmCallback, cancelCallback) {
        // Create modal backdrop
        const backdrop = document.createElement('div');
        backdrop.className = 'custom-modal-backdrop';
        backdrop.style.position = 'fixed';
        backdrop.style.top = '0';
        backdrop.style.left = '0';
        backdrop.style.width = '100%';
        backdrop.style.height = '100%';
        backdrop.style.backgroundColor = 'rgba(0,0,0,0.5)';
        backdrop.style.zIndex = '9998';
        backdrop.style.display = 'flex';
        backdrop.style.justifyContent = 'center';
        backdrop.style.alignItems = 'center';

        // Create modal dialog - dark theme with glass effect
        const modal = document.createElement('div');
        modal.className = 'custom-modal';
        modal.style.backgroundColor = 'rgba(25, 35, 50, 0.85)'; // Dark blue semi-transparent
        modal.style.backdropFilter = 'blur(10px)';
        modal.style.WebkitBackdropFilter = 'blur(10px)'; // For Safari
        modal.style.color = '#fff';
        modal.style.borderRadius = '8px';
        modal.style.width = '400px';
        modal.style.maxWidth = '90%';
        modal.style.boxShadow = '0 4px 20px rgba(0,0,0,0.4)';
        modal.style.animation = 'modalFadeIn 0.3s';
        modal.style.border = '1px solid rgba(255, 255, 255, 0.1)';

        // Add styles for modal animation if they don't exist yet
        if (!document.getElementById('custom-modal-styles')) {
            const styleEl = document.createElement('style');
            styleEl.id = 'custom-modal-styles';
            styleEl.textContent = `
                @keyframes modalFadeIn {
                    from { opacity: 0; transform: scale(0.95); }
                    to { opacity: 1; transform: scale(1); }
                }
            `;
            document.head.appendChild(styleEl);
        }

        // Add modal content - with amber (#ffaa00) warning icon and button
        modal.innerHTML = `
            <div style="padding: 20px; border-bottom: 1px solid rgba(255, 255, 255, 0.1);">
                <div style="display: flex; align-items: center;">
                    <i class="${icon}" style="margin-right: 12px; color: #ffaa00; font-size: 1.5rem;"></i>
                    <h3 style="margin: 0; font-size: 1.2rem; color: #fff;">${title}</h3>
                </div>
            </div>
            <div style="padding: 20px;">
                <p style="color: rgba(255, 255, 255, 0.9);">${message}</p>
            </div>
            <div style="padding: 15px 20px; border-top: 1px solid rgba(255, 255, 255, 0.1); text-align: right;">
                <button class="cancel-btn" style="background-color: rgba(255, 255, 255, 0.1); color: #fff; border: 1px solid rgba(255, 255, 255, 0.2); padding: 8px 16px; border-radius: 4px; margin-right: 10px; cursor: pointer;">Cancel</button>
                <button class="confirm-btn" style="background-color: #ffaa00; color: #000; border: none; padding: 8px 16px; border-radius: 4px; cursor: pointer; font-weight: 500;">Confirm</button>
            </div>
        `;

        // Add modal to backdrop
        backdrop.appendChild(modal);

        // Add backdrop to document
        document.body.appendChild(backdrop);

        // Add button event listeners
        const cancelBtn = modal.querySelector('.cancel-btn');
        const confirmBtn = modal.querySelector('.confirm-btn');

        // Close modal function
        const closeModal = () => {
            backdrop.style.opacity = '0';
            modal.style.opacity = '0';
            modal.style.transform = 'scale(0.95)';
            setTimeout(() => {
                if (backdrop.parentNode) {
                    backdrop.parentNode.removeChild(backdrop);
                }
            }, 300);
        };

        // Add hover effects to buttons
        cancelBtn.addEventListener('mouseover', () => {
            cancelBtn.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
        });
        cancelBtn.addEventListener('mouseout', () => {
            cancelBtn.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
        });

        confirmBtn.addEventListener('mouseover', () => {
            confirmBtn.style.backgroundColor = '#ffb52e'; // Slightly lighter amber on hover
        });
        confirmBtn.addEventListener('mouseout', () => {
            confirmBtn.style.backgroundColor = '#ffaa00';
        });

        // Cancel button
        cancelBtn.addEventListener('click', () => {
            closeModal();
            if (typeof cancelCallback === 'function') {
                cancelCallback();
            }
        });

        // Confirm button
        confirmBtn.addEventListener('click', () => {
            closeModal();
            if (typeof confirmCallback === 'function') {
                confirmCallback();
            }
        });

        // Close on backdrop click
        backdrop.addEventListener('click', (e) => {
            if (e.target === backdrop) {
                closeModal();
                if (typeof cancelCallback === 'function') {
                    cancelCallback();
                }
            }
        });
    },

    // Show a temporary success message for claim reset
    showResetSuccessMessage: function() {
        this._showCustomToast(
            'Success',
            'Claims reset to default examples.',
            'ph ph-check-circle',
            null,
            null
        );
    },

    collectFormData: function () {
        // Basic claim information
        const formData = {
            claimId: new URLSearchParams(window.location.search).get('claimId') || this.generateClaimId(),
            clientName: document.getElementById('clientName')?.value || '',
            policyNumber: document.getElementById('policyNumber')?.value || '',
            vrn: document.getElementById('vehicleRegistration')?.value || '',
            driverName: document.getElementById('driverName')?.value || '',
            description: document.getElementById('incidentDescription')?.value || '',

            // Date and time
            dateTime: document.getElementById('incidentDateTime')?.value || '',

            // Location
            location: document.getElementById('locationSearch')?.value || '',

            // Incident and damage details
            incidentType: document.getElementById('incident-type-value')?.value || '',
            incidentCause: document.getElementById('incident-cause-value')?.value || '',
            incidentSubcause: document.getElementById('incident-subcause-value')?.value || '',
            damageType: document.getElementById('damage-type-value')?.value || '',
            liability: document.getElementById('liability-value')?.value || '',
            estimatedCost: document.getElementById('estimated-cost-value')?.value || '',

            // Damage assessment questionnaire
            claimDamage: this.getSelectedRadioValue('claim-damage'),
            repaired: this.getSelectedRadioValue('repaired'),
            repairNetwork: this.getSelectedRadioValue('repair-network'),
            replacement: this.getSelectedRadioValue('replacement'),

            // Additional information
            additionalNotes: document.getElementById('additionalNotes')?.value || '',

            // Declaration status
            declarationChecked: document.getElementById('declarationCheck')?.checked || false,

            // Form completion status - save current accordion states
            accordionStatus: this.getAccordionStatuses()
        };

        return formData;
    },

    // Helper function to get selected radio button value
    getSelectedRadioValue: function (name) {
        const radios = document.getElementsByName(name);
        for (const radio of radios) {
            if (radio.checked) {
                return radio.id;
            }
        }
        return null;
    },

    // Get the current accordion statuses from ClaimAssistant if available
    getAccordionStatuses: function () {
        if (window.ClaimAssistant && ClaimAssistant.accordionStatus) {
            return { ...ClaimAssistant.accordionStatus };
        }
        return {};
    },
    // Save a draft claim
    saveDraftClaim: function (formData) {
        // Generate a claim ID if not provided
        if (!formData.claimId) {
            formData.claimId = this.generateClaimId();
        }

        // Add timestamp to track when it was saved
        formData.lastSaved = new Date().toISOString();

        // Get existing drafts
        const drafts = this.getDraftClaims();

        // Check if a draft with this ID already exists
        const existingIndex = drafts.findIndex(draft => draft.claimId === formData.claimId);

        if (existingIndex >= 0) {
            // Update existing draft
            drafts[existingIndex] = formData;
        } else {
            // Add new draft
            drafts.push(formData);
        }

        // Save back to localStorage
        localStorage.setItem(this.DRAFTS_STORAGE_KEY, JSON.stringify(drafts));

        console.log('Draft claim saved:', formData);

        return formData.claimId;
    },


    // Save a draft and add it to the claims list with Pending status
    saveDraftAsPartialClaim: function () {
        console.log('Saving draft as partial claim with all form and accordion data');

        // Collect comprehensive form data including all accordion data
        const formData = this.collectFormData();

        // If we don't have at least client name, show an error
        if (!formData.clientName) {
            alert('Please enter at least a client name before saving');
            return null;
        }

        // Create the draft claim object to appear in the table
        const draftClaim = {
            claimId: formData.claimId,
            clientName: formData.clientName,
            policyNumber: formData.policyNumber,
            status: 'Draft', // Special status for draft claims
            lastUpdate: this.formatCurrentDateTime(),
            incidentType: formData.incidentType || 'Unspecified',
            vrn: formData.vrn,
            driverName: formData.driverName,
            description: formData.description,
            isDraft: true // Flag to identify as a draft
        };

        // Add to claims array (it will appear in tables)
        const claims = this.getAllClaims();

        // Check if this claim ID already exists
        const existingIndex = claims.findIndex(claim => claim.claimId === formData.claimId);

        // CHANGE: Enhanced logic for handling existing claims
        if (existingIndex >= 0) {
            const existingClaim = claims[existingIndex];

            // Only replace if the existing claim is a draft or not submitted
            if (existingClaim.isDraft === true || !existingClaim.submitted) {
                claims[existingIndex] = draftClaim;
            } else {
                // If trying to save a draft over a submitted claim, generate a new ID instead
                console.log('Cannot save draft over submitted claim, generating new ID');
                draftClaim.claimId = this.generateClaimId();
                formData.claimId = draftClaim.claimId; // Update the formData ID too
                claims.push(draftClaim);

                // Update URL to reflect the new ID
                const newUrl = new URL(window.location);
                newUrl.searchParams.set('claimId', draftClaim.claimId);
                window.history.pushState({}, '', newUrl);
            }
        } else {
            // Add new claim
            claims.push(draftClaim);
        }

        // Save back to localStorage
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(claims));

        // Also save complete form data as a draft (for the form restoration)
        // This includes all accordion data and form field values
        this.saveDraftClaim(formData);

        console.log('Draft saved as partial claim with complete form data:', draftClaim);

        return draftClaim;
    },

    // Set up the save draft button
    setupSaveDraftButton: function () {
        const saveDraftBtn = document.getElementById('save-draft-claim');
        if (!saveDraftBtn) {
            console.log("Save draft button not found");
            return;
        }

        console.log("Save draft button found:", saveDraftBtn);

        // Store reference to 'this' for the callback
        const self = this;

        // Add click handler to the save draft button
        saveDraftBtn.addEventListener('click', function (e) {
            e.preventDefault();
            console.log("Save draft button clicked");

            const savedClaim = self.saveDraftAsPartialClaim();

            if (savedClaim) {
                // Show success toast
                self.showDraftSavedToast(savedClaim.claimId);
                console.log("Draft saved, toast triggered");

                // Show confirmation dialog for dashboard navigation
                self._showCustomConfirmDialog(
                    'Draft Saved',
                    'Your draft has been saved successfully. Would you like to return to the dashboard now?',
                    'ph ph-check-circle',
                    // Confirm callback (go to dashboard)
                    function () {
                        window.location.href = 'index.html';
                    },
                    // Cancel callback (stay on page)
                    function () {
                        console.log("User chose to continue editing");
                    }
                );
            } else {
                console.error("Failed to save draft claim");
            }
        });
    },

    // New function to show a toast notification for draft save
    showDraftSavedToast: function (claimId) {
        this._showCustomToast(
            'Draft Saved',
            `Claim <strong>${claimId}</strong> has been saved as a draft.`,
            'ph ph-floppy-disk',
            '#f8f9fa',  // Light gray background
            '#333'      // Dark text
        );
    },


    // Show success message after saving
    showSaveSuccessMessage: function (claimId) {
        // Reuse the draft saved toast for consistency
        this.showDraftSavedToast(claimId);
    },
    // Get all draft claims
    getDraftClaims: function () {
        const draftsData = localStorage.getItem(this.DRAFTS_STORAGE_KEY);
        return draftsData ? JSON.parse(draftsData) : [];
    },

    // Get a specific draft claim by ID
    getDraftClaimById: function (claimId) {
        const drafts = this.getDraftClaims();
        return drafts.find(draft => draft.claimId === claimId) || null;
    },

    // Delete a draft claim
    deleteDraftClaim: function (claimId) {
        const drafts = this.getDraftClaims();
        const filteredDrafts = drafts.filter(draft => draft.claimId !== claimId);

        localStorage.setItem(this.DRAFTS_STORAGE_KEY, JSON.stringify(filteredDrafts));
        console.log('Draft claim deleted:', claimId);
    },

    // Filter claims by client name
    getClaimsByClient: function (clientName) {
        const claims = this.getAllClaims();
        return claims.filter(claim => claim.clientName === clientName);
    },

    // Filter claims by policy number
    getClaimsByPolicy: function (policyNumber) {
        const claims = this.getAllClaims();
        return claims.filter(claim => claim.policyNumber === policyNumber);
    },

    // Render claims based on the current page
    renderClaimsForCurrentPage: function () {
        // Try to detect current page
        const currentPage = document.documentElement.getAttribute('data-page') || this.detectPageTypeFromURL();
        console.log('Rendering claims for page type:', currentPage);

        if (currentPage === 'fleet-insurance' || window.location.pathname.endsWith('index.html')) {
            // Main dashboard page - show all claims
            this.renderMainDashboardClaims();
        }
        else if (currentPage === 'client-page') {
            // Client-specific page - filter claims by client
            this.renderClientPageClaims();
        }
        else if (currentPage === 'policy-page') {
            // Policy-specific page - filter claims by policy
            this.renderPolicyPageClaims();
        }
        else {
            console.log('No claims rendering needed for this page type');

            // Check if we have a claims table and render all claims as a fallback
            const claimsTable = document.getElementById('claimsTable');
            if (claimsTable) {
                console.log('Claims table found but page type unknown, rendering all claims as fallback');
                this.renderMainDashboardClaims();
            }
        }
    },

    // Detect page type from URL if data-page attribute is not available
    detectPageTypeFromURL: function () {
        const path = window.location.pathname.toLowerCase();

        if (path.endsWith('index.html') || path.endsWith('/')) {
            return 'fleet-insurance';
        }
        else if (path.includes('sprint-courier') ||
            path.includes('value-car') ||
            path.includes('auto-driving') ||
            path.includes('fast-delivery')) {
            return 'client-page';
        }
        else if (path.includes('pol-') || path.includes('policy')) {
            return 'policy-page';
        }

        return 'unknown';
    },

    // Render claims for the main dashboard
    renderMainDashboardClaims: function () {
        const claimsTable = document.getElementById('claimsTable');
        if (!claimsTable) {
            console.error('Claims table not found in the DOM');
            return;
        }

        const tbody = claimsTable.querySelector('tbody');
        if (!tbody) {
            console.error('Table body not found in claims table');
            return;
        }

        // Get all claims
        const claims = this.getAllClaims();
        console.log('Rendering dashboard claims, found:', claims.length);

        // Clear existing rows
        tbody.innerHTML = '';

        // Add claims to the table
        claims.forEach(claim => {
            const row = document.createElement('tr');
            row.setAttribute('data-client', claim.clientName);
            row.style.cursor = 'pointer';

            // Get colors for the status badge
            const colors = this.getStatusColor(claim.status);

            // Determine if this is a clickable claim ID (only drafts are clickable)
            const claimIdClass = claim.isDraft ? "claim-id-link" : "claim-id-readonly";

            // Create row HTML with updated badge and conditionally clickable claim ID
            row.innerHTML = `
                <th scope="row" class="${claimIdClass}">${claim.claimId}</th>
                <td>${claim.clientName}</td>
                <td><span class="badge text-${colors.text} w-50" style="background-color: ${colors.bg};">${claim.status}</span></td>
                <td>${claim.lastUpdate}</td>
                <td>${claim.incidentType}</td>
            `;

            // Add click event to navigate to client page
            row.addEventListener('click', () => {
                // Find client page URL based on client name
                const clientPageUrl = this.getClientPageUrl(claim.clientName);
                if (clientPageUrl) {
                    window.location.href = clientPageUrl;
                }
            });

            tbody.appendChild(row);
        });

        // Make claim IDs clickable (only for drafts)
        this.makeClaimIdsClickable();
    },

    // Render claims for a client-specific page
    renderClientPageClaims: function () {
        const claimsTable = document.getElementById('claimsTable');
        if (!claimsTable) return;

        const tbody = claimsTable.querySelector('tbody');
        if (!tbody) return;

        // Get client name from page
        const clientName = this.getClientNameFromPage();
        if (!clientName) {
            console.error('Could not determine client name from page');
            return;
        }

        console.log('Rendering claims for client:', clientName);

        // Get claims for this client
        const claims = this.getClaimsByClient(clientName);

        // Clear existing rows
        tbody.innerHTML = '';

        // Add claims to the table
        claims.forEach(claim => {
            const row = document.createElement('tr');

            // Get colors for the status badge
            const colors = this.getStatusColor(claim.status);

            // Determine if this is a clickable claim ID (only drafts are clickable)
            const claimIdClass = claim.isDraft ? "claim-id-link" : "claim-id-readonly";

            // Create row HTML - client page shows policy number instead of client name
            row.innerHTML = `
                <th scope="row" class="${claimIdClass}">${claim.claimId}</th>
                <td>${claim.policyNumber}</td>
                <td><span class="badge text-${colors.text} w-50" style="background-color: ${colors.bg};">${claim.status}</span></td>
                <td>${claim.lastUpdate}</td>
                <td>${claim.incidentType}</td>
            `;

            tbody.appendChild(row);
        });

        // Make claim IDs clickable (only for drafts)
        this.makeClaimIdsClickable();
    },

    // Render claims for a policy-specific page
    renderPolicyPageClaims: function () {
        const claimsTable = document.getElementById('claimsTable');
        if (!claimsTable) return;

        const tbody = claimsTable.querySelector('tbody');
        if (!tbody) return;

        // Get policy number from page
        const policyNumber = this.getPolicyNumberFromPage();
        if (!policyNumber) {
            console.error('Could not determine policy number from page');
            return;
        }

        console.log('Rendering claims for policy:', policyNumber);

        // Get claims for this policy
        const claims = this.getClaimsByPolicy(policyNumber);

        // Clear existing rows
        tbody.innerHTML = '';

        // Add claims to the table
        claims.forEach(claim => {
            const row = document.createElement('tr');

            // Get colors for the status badge
            const colors = this.getStatusColor(claim.status);

            // Determine if this is a clickable claim ID (only drafts are clickable)
            const claimIdClass = claim.isDraft ? "claim-id-link" : "claim-id-readonly";

            // Create row HTML - policy page layout
            row.innerHTML = `
                <th scope="row" class="${claimIdClass}">${claim.claimId}</th>
                <td>${claim.policyNumber}</td>
                <td><span class="badge text-${colors.text} w-50" style="background-color: ${colors.bg};">${claim.status}</span></td>
                <td>${claim.lastUpdate}</td>
                <td>${claim.incidentType}</td>
            `;

            tbody.appendChild(row);
        });

        // Make claim IDs clickable (only for drafts)
        this.makeClaimIdsClickable();
    },

    // Make claim IDs clickable in the claims tables
    makeClaimIdsClickable: function () {
        const claimsTable = document.getElementById('claimsTable');
        if (!claimsTable) return;

        // Get all claims
        const claims = this.getAllClaims();

        // Find all claim ID cells
        const claimIdCells = claimsTable.querySelectorAll('tbody th.claim-id-link');

        claimIdCells.forEach(cell => {
            const claimId = cell.textContent.trim();

            // Find the matching claim to check if it's a draft
            const claim = claims.find(c => c.claimId === claimId);

            // Only make drafts clickable
            if (claim && claim.isDraft === true) {
                cell.style.cursor = 'pointer';

                // Add click handler
                cell.addEventListener('click', (e) => {
                    e.stopPropagation(); // Prevent row click from firing
                    // Navigate to the claim form with the claim ID
                    window.location.href = `policy-details.html?claimId=${claimId}`;
                });
            } else {
                // For submitted claims, remove any existing click handlers and cursor style
                cell.style.cursor = 'default';

                // Remove any existing click event listeners by cloning and replacing the element
                const newCell = cell.cloneNode(true);
                cell.parentNode.replaceChild(newCell, cell);
            }
        });
    },

    // Check for a draft claim ID in the URL and load it if found
    checkForDraftClaimInURL: function () {
        // Check if we're on the policy-details.html page
        if (!window.location.pathname.toLowerCase().includes('policy-details')) {
            return;
        }

        // Check for claim ID in URL
        const urlParams = new URLSearchParams(window.location.search);
        const claimId = urlParams.get('claimId');

        if (!claimId) return;

        console.log('Found claim ID in URL:', claimId);

        // Try to load the draft claim
        const draftClaim = this.getDraftClaimById(claimId);

        if (draftClaim) {
            console.log('Found draft claim:', draftClaim);
            this.restoreDraftClaim(draftClaim);
        } else {
            // Check if it's a submitted claim that we want to edit
            const submittedClaim = this.getClaimById(claimId);

            if (submittedClaim) {
                // CHANGE: Check if the claim is a submitted claim (not a draft)
                if (submittedClaim.submitted === true || submittedClaim.isDraft !== true) {
                    console.log('Attempted to edit a submitted claim:', claimId);

                    // Show error message
                    this.showSubmittedClaimErrorMessage(claimId);

                    // Optionally redirect back to dashboard after a short delay
                    setTimeout(() => {
                        window.location.href = 'index.html';
                    }, 3000);

                    return;
                }

                console.log('Found submitted claim to edit:', submittedClaim);
                this.restoreSubmittedClaim(submittedClaim);
            } else {
                console.log('No draft or submitted claim found with ID:', claimId);
            }
        }
    },

    showSubmittedClaimErrorMessage: function (claimId) {
        // Create a message container
        const messageContainer = document.createElement('div');
        messageContainer.className = 'alert alert-danger alert-dismissible fade show';
        messageContainer.style.position = 'sticky';
        messageContainer.style.top = '0';
        messageContainer.style.zIndex = '1000';

        // Set message content
        messageContainer.innerHTML = `
            <i class="ph ph-x-circle me-2"></i>
            <strong>Cannot Edit Submitted Claim!</strong> Claim ${claimId} has already been submitted and cannot be edited.
            <p class="mt-2 mb-0">You will be redirected to the dashboard in a few seconds.</p>
            <button type="button" class="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
        `;

        // Insert at the top of the content
        const contentBody = document.querySelector('.content-body');
        if (contentBody) {
            contentBody.insertBefore(messageContainer, contentBody.firstChild);
        } else {
            document.body.insertBefore(messageContainer, document.body.firstChild);
        }
    },

    // Restore a draft claim to the form
    restoreDraftClaim: function (draftClaim) {
        console.log('Restoring draft claim with accordion status preservation:', draftClaim);

        // Populate all form fields including accordions
        this.populateFormFields(draftClaim);

        // Show notification that we're restoring a draft
        this.showRestoreMessage(draftClaim.claimId, true);

        // After restoration is complete, ensure accordions reflect the proper state
        setTimeout(() => {
            // If ClaimAssistant is available, ensure accordion status is correctly reflected
            if (window.ClaimAssistant) {
                // Refresh accordion statuses for all sections
                this.updateAllAccordionStatuses();

                // If we have saved accordion status, apply it
                if (draftClaim.accordionStatus) {
                    // Apply each saved status
                    Object.keys(draftClaim.accordionStatus).forEach(sectionId => {
                        const status = draftClaim.accordionStatus[sectionId];
                        const section = document.getElementById(sectionId);

                        if (section && ClaimAssistant.updateAccordionStatus) {
                            ClaimAssistant.updateAccordionStatus(sectionId, status);
                        }
                    });
                }
            }
        }, 1000); // Give enough time for all form elements to be processed
    },

    updateAllAccordionStatuses: function () {
        if (!window.ClaimAssistant || !ClaimAssistant.accordionStatus) return;

        // Go through all accordions and update their status based on content
        const formData = this.collectFormData();
        this.updateAccordionStatusesAfterRestore(formData);
    },

    // Restore a submitted claim to the form for editing
    restoreSubmittedClaim: function (submittedClaim) {
        console.log('Restoring submitted claim with accordion handling:', submittedClaim);

        // First convert to a draft format with all needed fields
        const draftFormat = {
            claimId: submittedClaim.claimId,
            clientName: submittedClaim.clientName,
            policyNumber: submittedClaim.policyNumber,
            vrn: submittedClaim.vrn,
            driverName: submittedClaim.driverName,
            description: submittedClaim.description,
            incidentType: submittedClaim.incidentType,
            // For submitted claims, mark all relevant accordions as done
            accordionStatus: {
                'collapseOne': 'done',
                'collapseTwo': 'done',
                'collapseThree': 'done',
                'collapseThreeB': 'done',
                'collapseFour': 'done',
                'collapseFive': 'done',
                'collapseSix': 'done',
                'collapseSeven': 'done',
                'collapseEight': 'done',
                'collapseNine': 'done',
                'collapseTen': 'done',
                'collapseEleven': 'done',
                'collapseTwelve': 'done',
                'collapseThirteen': 'done'
            }
        };

        // Use the draft restoration logic
        this.populateFormFields(draftFormat);

        // Show notification that we're editing a submitted claim
        this.showRestoreMessage(submittedClaim.claimId, false);

        // After restoration is complete, ensure accordions reflect the proper state
        setTimeout(() => {
            // If ClaimAssistant is available, update all accordions to show as complete
            if (window.ClaimAssistant && ClaimAssistant.updateAccordionStatus) {
                Object.keys(draftFormat.accordionStatus).forEach(sectionId => {
                    const section = document.getElementById(sectionId);
                    if (section) {
                        ClaimAssistant.updateAccordionStatus(sectionId, 'done');
                    }
                });
            }
        }, 1000);
    },

    // Populate form fields with claim data
    populateFormFields: function (claimData) {
        console.log('Populating all form fields with saved data:', claimData);

        // Basic fields
        const textFields = {
            'clientName': claimData.clientName || '',
            'policyNumber': claimData.policyNumber || '',
            'vehicleRegistration': claimData.vrn || '',
            'driverName': claimData.driverName || '',
            'incidentDescription': claimData.description || '',
            'incidentDateTime': claimData.dateTime || '',
            'locationSearch': claimData.location || '',
            'additionalNotes': claimData.additionalNotes || ''
        };

        // Set each text field value
        Object.keys(textFields).forEach(fieldId => {
            const field = document.getElementById(fieldId);
            if (field) {
                field.value = textFields[fieldId];
            }
        });

        // Handle select elements
        const selectFields = {
            'incident-type-value': claimData.incidentType || '',
            'incident-cause-value': claimData.incidentCause || '',
            'incident-subcause-value': claimData.incidentSubcause || '',
            'damage-type-value': claimData.damageType || '',
            'liability-value': claimData.liability || '',
            'estimated-cost-value': claimData.estimatedCost || ''
        };

        // Set each select field value
        Object.keys(selectFields).forEach(fieldId => {
            const field = document.getElementById(fieldId);
            if (field && selectFields[fieldId]) {
                // Find and select the matching option
                const options = Array.from(field.options);
                const matchingOption = options.find(option =>
                    option.value === selectFields[fieldId] ||
                    option.textContent.trim() === selectFields[fieldId]
                );

                if (matchingOption) {
                    matchingOption.selected = true;
                    // Trigger change event to update any dependent fields
                    field.dispatchEvent(new Event('change'));
                }
            }
        });

        // Handle radio buttons
        if (claimData.claimDamage) this.setRadioButton(claimData.claimDamage);
        if (claimData.repaired) this.setRadioButton(claimData.repaired);
        if (claimData.repairNetwork) this.setRadioButton(claimData.repairNetwork);
        if (claimData.replacement) this.setRadioButton(claimData.replacement);

        // Handle declaration checkbox
        const declarationCheck = document.getElementById('declarationCheck');
        if (declarationCheck && claimData.declarationChecked) {
            declarationCheck.checked = true;
            declarationCheck.dispatchEvent(new Event('change'));
        }

        // Restore accordion statuses if saved
        this.restoreAccordionStatuses(claimData.accordionStatus);

        // If available, trigger the ClaimAssistant analysis for the incident description
        if (window.ClaimAssistant && typeof ClaimAssistant.analyzeText === 'function') {
            const incidentDescription = document.getElementById('incidentDescription');
            if (incidentDescription && incidentDescription.value) {
                ClaimAssistant.analyzeText(incidentDescription.value);
            }
        }

        // Manually update location display if location is provided
        if (claimData.location) {
            this.updateLocationMap(claimData.location);
        }

        // Force update all accordion statuses after restoration
        setTimeout(() => {
            this.updateAccordionStatusesAfterRestore(claimData);
        }, 500);
    },

    // Helper function to set a radio button by ID
    setRadioButton: function (radioId) {
        const radio = document.getElementById(radioId);
        if (radio) {
            radio.checked = true;
            radio.dispatchEvent(new Event('change'));
        }
    },

    updateLocationMap: function (location) {
        const mapContainer = document.getElementById('incidentMap');
        if (!mapContainer) return;

        const mapDefaultImage = mapContainer.querySelector('.map-image-default');
        const mapLocationImage = mapContainer.querySelector('.map-image-location');
        const mapPin = mapContainer.querySelector('.map-pin');
        const locationTextElement = mapContainer.querySelector('#map-location-text');

        // Display location map and hide default map
        if (mapDefaultImage) mapDefaultImage.style.display = "none";
        if (mapLocationImage) mapLocationImage.style.display = "block";

        // Update location text and show pin
        if (locationTextElement) {
            locationTextElement.textContent = `Location: ${location}`;
        }

        if (mapPin) {
            mapPin.style.display = "block";

            // Generate a position based on the location string
            const hash = location.split('').reduce((a, b) => {
                a = ((a << 5) - a) + b.charCodeAt(0);
                return a & a;
            }, 0);

            const topPercent = 30 + Math.abs(hash % 40);
            const leftPercent = 30 + Math.abs((hash >> 4) % 40);

            mapPin.style.top = `${topPercent}%`;
            mapPin.style.left = `${leftPercent}%`;
        }
    },

    // Restore accordion statuses from saved data
    restoreAccordionStatuses: function (savedStatuses) {
        if (!savedStatuses || !window.ClaimAssistant) return;

        // Update ClaimAssistant's accordion status with saved values
        if (window.ClaimAssistant.accordionStatus) {
            Object.keys(savedStatuses).forEach(sectionId => {
                // Only update if the section exists in the current page
                if (document.getElementById(sectionId)) {
                    ClaimAssistant.accordionStatus[sectionId] = savedStatuses[sectionId];
                }
            });
        }

        // Re-initialize accordion checkmarks if that function exists
        if (typeof ClaimAssistant.initAccordionCheckmarks === 'function') {
            ClaimAssistant.initAccordionCheckmarks();
        }
    },

    // Force update accordion statuses based on form values
    updateAccordionStatusesAfterRestore: function (claimData) {
        if (!window.ClaimAssistant || !ClaimAssistant.updateAccordionStatus) return;

        // Update each accordion section based on its content

        // Policy and Insured Details (collapseOne)
        if (claimData.clientName && claimData.policyNumber && claimData.vrn && claimData.driverName) {
            ClaimAssistant.updateAccordionStatus('collapseOne', 'done');
        }

        // Incident Description (collapseTwo)
        if (claimData.description && claimData.description.length > 20) {
            ClaimAssistant.updateAccordionStatus('collapseTwo', 'done');
        }

        // Date and Time (collapseThree)
        if (claimData.dateTime) {
            ClaimAssistant.updateAccordionStatus('collapseThree', 'done');
        }

        // Location (collapseThreeB)
        if (claimData.location) {
            ClaimAssistant.updateAccordionStatus('collapseThreeB', 'done');
        }

        // Damage and Liability (collapseFour)
        if (claimData.incidentType && claimData.liability) {
            ClaimAssistant.updateAccordionStatus('collapseFour', 'done');
        }

        // Damage Assessment Questionnaire (collapseFive)
        if (claimData.claimDamage && claimData.repaired && claimData.repairNetwork && claimData.replacement) {
            ClaimAssistant.updateAccordionStatus('collapseFive', 'done');
        }

        // Additional Notes (collapseNine)
        if (claimData.additionalNotes && claimData.additionalNotes.length > 0) {
            ClaimAssistant.updateAccordionStatus('collapseNine', 'done');
        }

        // Declaration (collapseTen)
        if (claimData.declarationChecked) {
            ClaimAssistant.updateAccordionStatus('collapseTen', 'done');
        }
    },

    // Show message about restoring a claim
    showRestoreMessage: function (claimId, isDraft) {
        // Create a message container if not exists
        let messageContainer = document.getElementById('restore-message-container');

        if (!messageContainer) {
            messageContainer = document.createElement('div');
            messageContainer.id = 'restore-message-container';
            messageContainer.className = 'alert alert-info alert-dismissible fade show';
            messageContainer.style.position = 'sticky';
            messageContainer.style.top = '0';
            messageContainer.style.zIndex = '1000';

            // Add close button
            messageContainer.innerHTML = `
                <button type="button" class="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
            `;

            // Insert at the top of the content
            const contentBody = document.querySelector('.content-body');
            if (contentBody) {
                contentBody.insertBefore(messageContainer, contentBody.firstChild);
            } else {
                document.body.insertBefore(messageContainer, document.body.firstChild);
            }
        }

        // Set message content
        if (isDraft) {
            messageContainer.innerHTML = `
                <i class="ph ph-info-circle me-2"></i>
                <strong>Draft Restored!</strong> You're working on a saved draft of claim ${claimId}.
                <button type="button" class="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
            `;
        } else {
            messageContainer.innerHTML = `
                <i class="ph ph-pencil me-2"></i>
                <strong>Editing Claim!</strong> You're editing the submitted claim ${claimId}.
                <button type="button" class="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
            `;
        }
    },

    // Get client name from the current page
    getClientNameFromPage: function () {
        // Try to get from breadcrumb
        const breadcrumbItems = document.querySelectorAll('.breadcrumb-item');
        for (const item of breadcrumbItems) {
            if (item.textContent.includes('Sprint Courier Ltd')) {
                return 'Sprint Courier Ltd';
            }
            if (item.textContent.includes('Value Car Rental UK')) {
                return 'Value Car Rental UK';
            }
            if (item.textContent.includes('Auto Driving School')) {
                return 'Auto Driving School';
            }
            if (item.textContent.includes('Fast-Food Deliveries')) {
                return 'Fast-Food Deliveries';
            }
        }

        // Try to get from URL
        const pathname = window.location.pathname.toLowerCase();

        if (pathname.includes('sprint-courier')) return 'Sprint Courier Ltd';
        if (pathname.includes('value-car')) return 'Value Car Rental UK';
        if (pathname.includes('auto-driving')) return 'Auto Driving School';
        if (pathname.includes('fast-delivery')) return 'Fast-Food Deliveries';

        // Try to get from page title
        const title = document.title;
        if (title.includes('Sprint Courier')) return 'Sprint Courier Ltd';
        if (title.includes('Value Car')) return 'Value Car Rental UK';
        if (title.includes('Auto Driving')) return 'Auto Driving School';
        if (title.includes('Fast-Food')) return 'Fast-Food Deliveries';

        return null;
    },

    // Get policy number from the current page
    getPolicyNumberFromPage: function () {
        // Try to get from breadcrumb
        const breadcrumbItems = document.querySelectorAll('.breadcrumb-item');
        for (const item of breadcrumbItems) {
            if (item.textContent.includes('POL-')) {
                const matches = item.textContent.match(/POL-\d+/);
                if (matches) return matches[0];
            }
        }

        // Try to get from URL
        const pathname = window.location.pathname.toLowerCase();

        if (pathname.includes('pol-1001')) return 'POL-1001';
        if (pathname.includes('pol-1002')) return 'POL-1002';
        if (pathname.includes('pol-1003')) return 'POL-1003';
        if (pathname.includes('pol-1004')) return 'POL-1004';

        // Try to get from page title
        const title = document.title;
        const policyMatch = title.match(/POL-\d+/);
        if (policyMatch) return policyMatch[0];

        return null;
    },

    // Get client page URL based on client name
    getClientPageUrl: function (clientName) {
        switch (clientName) {
            case 'Sprint Courier Ltd':
                return 'sprint-courier.html';
            case 'Value Car Rental UK':
                return 'value-car.html';
            case 'Auto Driving School':
                return 'auto-driving.html';
            case 'Fast-Food Deliveries':
                return 'fast-delivery.html';
            default:
                return null;
        }
    },

    // Get colors for status badge (background and text)
    getStatusColor: function (status) {
        const colors = {
            'Submitted': { bg: '#E7E7E7', text: 'dark' },
            'Processing': { bg: '#A3A8B2', text: 'dark' },
            'Approved': { bg: '#8d95ca', text: 'dark' },
            'Awaiting Repair': { bg: '#545E85', text: 'white' },
            'Completed': { bg: '#101B4B', text: 'white' },
            'Draft': { bg: '#B8C2CC', text: 'dark' },    // Special styling for drafts
            'default': { bg: '#E7E7E7', text: 'dark' }
        };

        return colors[status] || colors.default;
    },

    // Set up table search functionality
    setupTableSearch: function () {
        const tableSearch = document.getElementById('tableSearch');
        if (!tableSearch) return;

        tableSearch.addEventListener('input', () => {
            const searchTerm = tableSearch.value.toLowerCase().trim();
            const claimsTable = document.getElementById('claimsTable');
            if (!claimsTable) return;

            const rows = claimsTable.querySelectorAll('tbody tr');
            rows.forEach(row => {
                const text = row.textContent.toLowerCase();
                row.style.display = text.includes(searchTerm) ? '' : 'none';
            });
        });
    },

    // Set up reset button click handler
    setupResetButton: function() {
        const resetBtn = document.getElementById('resetClaimsBtn');
        if (!resetBtn) return;
        
        console.log("Reset button found:", resetBtn);
        
        // Store reference to 'this' for the callback
        const self = this;
        
        // Add click handler to open the confirmation dialog
        resetBtn.addEventListener('click', function(e) {
            e.preventDefault();
            console.log("Reset button clicked");
            
            self._showCustomConfirmDialog(
                'Reset Claims',
                'Are you sure you want to reset claims to the default examples? All newly added claims will be removed and this action cannot be undone.',
                'ph ph-warning-circle',
                // Confirm callback
                function() {
                    self.resetClaims();
                    console.log("Claims reset confirmed");
                },
                // Cancel callback
                function() {
                    console.log("Claims reset cancelled");
                }
            );
        });
    },

    // Set up handler for claim submission
    setupClaimSubmissionHandler: function () {
        // Check if we're on the policy-details.html page
        const currentPath = window.location.pathname.toLowerCase();
        if (currentPath.includes('policy-details')) {
            // Find the confirm submission button in the modal
            const confirmBtn = document.getElementById('confirm-submission');
            if (!confirmBtn) {
                // If it doesn't exist yet (might be added dynamically), add a mutation observer
                this.setupModalObserver();
                return;
            }

            // Add click handler to the confirm button
            confirmBtn.addEventListener('click', this.handleClaimSubmission.bind(this));
        }

        // Also look for the go-to-dashboard button that might be in the modal after submission
        const dashboardBtn = document.getElementById('go-to-dashboard');
        if (dashboardBtn) {
            dashboardBtn.addEventListener('click', () => {
                window.location.href = 'index.html';
            });
        }
    },

    // Set up an observer to watch for the modal buttons to be added to the DOM
    setupModalObserver: function () {
        const modalBody = document.querySelector('#verticallyCenter .modal-body');
        if (!modalBody) return;

        // Create a mutation observer
        const observer = new MutationObserver((mutations) => {
            for (const mutation of mutations) {
                if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
                    // Look for the confirm button
                    const confirmBtn = document.getElementById('confirm-submission');
                    if (confirmBtn) {
                        confirmBtn.addEventListener('click', this.handleClaimSubmission.bind(this));
                        observer.disconnect(); // Stop observing once we've found the button
                        break;
                    }
                }
            }
        });

        // Start observing the modal body
        observer.observe(modalBody.parentElement, { childList: true, subtree: true });
    },

    // Handle claim submission from the confirmation button
    handleClaimSubmission: function () {
        console.log('Handling claim submission');

        // Collect form data
        const clientName = document.getElementById('clientName').value.trim();
        const policyNumber = document.getElementById('policyNumber').value;
        const vrn = document.getElementById('vehicleRegistration').value.trim();
        const driverName = document.getElementById('driverName').value.trim();
        const incidentDescription = document.getElementById('incidentDescription').value.trim();

        // Get incident type from the form if available, or extract from description
        let incidentType = '';
        const incidentTypeElement = document.getElementById('incident-type-value');
        if (incidentTypeElement && incidentTypeElement.value) {
            incidentType = incidentTypeElement.value;
        } else {
            // Extract from description as fallback
            if (incidentDescription.toLowerCase().includes('accident')) {
                incidentType = 'Accident';
            } else if (incidentDescription.toLowerCase().includes('theft')) {
                incidentType = 'Theft';
            } else if (incidentDescription.toLowerCase().includes('windscreen')) {
                incidentType = 'Windscreen';
            } else if (incidentDescription.toLowerCase().includes('vandalism')) {
                incidentType = 'Vandalism';
            } else {
                incidentType = 'Accident'; // Default
            }
        }

        // Get claim ID from URL - this is CRITICAL for preserving the same ID
        const urlParams = new URLSearchParams(window.location.search);
        let claimId = urlParams.get('claimId');

        if (!claimId) {
            claimId = this.generateClaimId();
        }

        // IMPORTANT: Before submitting, check if this claim exists as a draft in the main claims list
        // and remove it to avoid duplicates
        const currentClaims = this.getAllClaims();
        const updatedClaims = currentClaims.filter(claim => !(claim.claimId === claimId && claim.isDraft === true));

        // Create the new claim object
        const newClaim = {
            claimId: claimId,
            clientName: clientName,
            policyNumber: policyNumber,
            status: 'Submitted',
            lastUpdate: this.formatCurrentDateTime(),
            incidentType: incidentType,
            vrn: vrn,
            driverName: driverName,
            description: incidentDescription,
            submitted: true // Add a flag to indicate this claim has been submitted
        };

        // Add the claim directly to our filtered claims array
        updatedClaims.push(newClaim);

        // Save the updated claims back to storage
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(updatedClaims));

        // CHANGE: Make sure to delete any draft with this claim ID
        this.deleteDraftClaim(claimId);

        console.log('Claim submitted:', newClaim);
        console.log('Draft with ID', claimId, 'removed after submission');

        // Update the modal to show success
        this.updateModalForSuccess(newClaim.claimId);
    },

    // Update modal to show success message
    updateModalForSuccess: function (claimId) {
        const modalBody = document.querySelector('#verticallyCenter .modal-body');
        const modalFooter = document.querySelector('#verticallyCenter .modal-footer');

        if (!modalBody || !modalFooter) return;

        // Update body with success message
        modalBody.innerHTML = `
            <div class="alert alert-success mb-4">
                <i class="ph ph-check-circle me-2"></i>
                Claim submitted successfully!
            </div>
            <p>Your claim has been submitted and is now being processed.</p>
            <p>A confirmation email will be sent to the registered address shortly.</p>
            <p class="mt-3">Claim Reference: <strong>${claimId}</strong></p>
        `;

        // Update footer with dashboard button
        modalFooter.innerHTML = `
            <button type="button" class="btn btn-success" id="go-to-dashboard">View Dashboard</button>
        `;

        // Add click handler to the dashboard button
        const dashboardBtn = document.getElementById('go-to-dashboard');
        if (dashboardBtn) {
            dashboardBtn.addEventListener('click', () => {
                window.location.href = 'index.html';
            });
        }
    },

    setupAutoSave: function () {
        // Check if we're on the policy-details.html page
        if (!window.location.pathname.toLowerCase().includes('policy-details')) {
            return;
        }

        // Get essential form elements to use as triggers for auto-save
        const formElements = [
            document.getElementById('clientName'),
            document.getElementById('policyNumber'),
            document.getElementById('vehicleRegistration'),
            document.getElementById('driverName'),
            document.getElementById('incidentDescription'),
            document.getElementById('incidentDateTime'),
            document.getElementById('locationSearch'),
            document.getElementById('incident-type-value'),
            document.getElementById('damage-type-value'),
            document.getElementById('additionalNotes')
        ];

        // Filter out null elements
        const validFormElements = formElements.filter(el => el !== null);

        if (validFormElements.length === 0) {
            console.log('No form elements found for auto-save');
            return;
        }

        // Get or generate a claim ID from URL
        const urlParams = new URLSearchParams(window.location.search);
        let claimId = urlParams.get('claimId');

        if (!claimId) {
            claimId = this.generateClaimId();
            // Update URL without reloading the page
            const newUrl = new URL(window.location);
            newUrl.searchParams.set('claimId', claimId);
            window.history.pushState({}, '', newUrl);
        }

        // Auto-save function that collects all form data
        const autoSave = () => {
            // Get complete form data including all accordion sections
            const formData = this.collectFormData();

            // Only save if we have at least some essential data
            if (formData.clientName || formData.description) {
                this.saveDraftClaim(formData);
                console.log('Auto-saved draft claim with comprehensive data:', claimId);
            }
        };

        // Debounce function to prevent too many saves
        const debounce = (func, delay) => {
            let timeoutId;
            return function (...args) {
                clearTimeout(timeoutId);
                timeoutId = setTimeout(() => func.apply(this, args), delay);
            };
        };

        // Debounced auto-save function
        const debouncedAutoSave = debounce(autoSave, 2000);

        // Add listeners to all form elements
        validFormElements.forEach(element => {
            if (element.tagName === 'SELECT' || element.tagName === 'TEXTAREA' ||
                (element.tagName === 'INPUT' && element.type !== 'radio' && element.type !== 'checkbox')) {
                // For text inputs, textareas, and selects
                element.addEventListener('input', debouncedAutoSave);
                element.addEventListener('change', debouncedAutoSave);
            } else if (element.tagName === 'INPUT' && (element.type === 'radio' || element.type === 'checkbox')) {
                // For radio buttons and checkboxes
                element.addEventListener('change', debouncedAutoSave);
            }
        });

        // Also track radio button changes for questionnaires
        const radioGroups = [
            'claim-damage',
            'repaired',
            'repair-network',
            'replacement'
        ];

        radioGroups.forEach(groupName => {
            const radios = document.getElementsByName(groupName);
            Array.from(radios).forEach(radio => {
                radio.addEventListener('change', debouncedAutoSave);
            });
        });

        // Handle declaration checkbox separately
        const declarationCheck = document.getElementById('declarationCheck');
        if (declarationCheck) {
            declarationCheck.addEventListener('change', debouncedAutoSave);
        }

        // Set up a periodic auto-save every 30 seconds as a fallback
        const periodicAutoSave = setInterval(autoSave, 30000);

        // Clear interval when navigating away
        window.addEventListener('beforeunload', () => {
            clearInterval(periodicAutoSave);
            // Do a final save before leaving
            autoSave();
        });

        // Also save on key navigation accordion events
        document.querySelectorAll('.accordion-button').forEach(button => {
            button.addEventListener('click', debouncedAutoSave);
        });

        // Save when submit or save draft buttons are clicked
        const submitBtn = document.getElementById('submit-claim');
        if (submitBtn) {
            submitBtn.addEventListener('click', autoSave);
        }

        const saveDraftBtn = document.getElementById('save-draft-claim');
        if (saveDraftBtn) {
            saveDraftBtn.addEventListener('click', autoSave);
        }
    }
};

// Initialize the claims manager when the DOM is ready
document.addEventListener('DOMContentLoaded', function () {
    console.log('DOM ready, initializing ClaimsManager');
    ClaimsManager.init();
});

// Also expose ClaimsManager globally for debugging
window.ClaimsManager = ClaimsManager;